import React, { useCallback, useEffect, useState, useRef } from "react";
import cn from "classnames";
import { createPortal } from "react-dom";
import { disableBodyScroll, clearAllBodyScrollLocks } from "body-scroll-lock";
import SignatureCanvas from "react-signature-canvas";
import styles from "./ModalCloseSession.module.sass";
import Icon from "../Icon";
import Card from "../Card";
import { showSuccess, showError } from "../../services/toast.service";

import { closeSession } from '../../services/sessions.service';

const ModalCloseSessionFormation = ({ visible, onClose, session, onSessionClosed }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [canvasWidth, setCanvasWidth] = useState(500);
  const signatureRef = useRef(null);

  // Calculer la largeur du canvas en fonction de la taille de l'écran
  useEffect(() => {
    const updateCanvasWidth = () => {
      if (typeof window !== 'undefined') {
        const screenWidth = window.innerWidth;
        if (screenWidth < 768) {
          setCanvasWidth(Math.min(screenWidth - 100, 450));
        } else {
          setCanvasWidth(500);
        }
      }
    };

    if (visible) {
      updateCanvasWidth();
      window.addEventListener('resize', updateCanvasWidth);
      return () => window.removeEventListener('resize', updateCanvasWidth);
    }
  }, [visible]);

  const escFunction = useCallback(
    (e) => {
      if (e.keyCode === 27) {
        onClose();
      }
    },
    [onClose]
  );

  const clearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
  };

  const handleCloseSession = async () => {
    if (!session?.id) {
      showError("Session non trouvée");
      return;
    }
  
    if (signatureRef.current?.isEmpty()) {
      showError("Veuillez signer avant de clôturer la session");
      return;
    }
    
    try {
      setIsClosing(true);
      
      const signatureDataURL = signatureRef.current.toDataURL();
      const blob = await (await fetch(signatureDataURL)).blob();
      const signatureFile = new File([blob], `signature_${session.id}.png`, { type: 'image/png' });
      
      const result = await closeSession(session.id, signatureFile);
      
      if (!result) {
        throw new Error("Échec de la clôture de la session");
      }
      
      showSuccess("Session clôturée avec succès");
      onSessionClosed?.(session.id);
      setTimeout(() => onClose(), 500);
    } catch (error) {
      showError(`Erreur: ${error.message}`);
    } finally {
      setIsClosing(false);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", escFunction, false);
    return () => {
      document.removeEventListener("keydown", escFunction, false);
    };
  }, [escFunction]);

  useEffect(() => {
    if (visible) {
      const target = document.querySelector("#modal-close-session-formation");
      disableBodyScroll(target);
    } else {
      clearAllBodyScrollLocks();
    }
  }, [visible]);

  return createPortal(
    visible && (
      <div id="modal-close-session-formation" className={styles.modal}>
        <div className={styles.control}>
          <button className={styles.close} onClick={onClose}>
            <Icon name="close" size="20" />
          </button>
        </div>
        <div className={styles.outer} style={{ position: 'relative' }}>
          <Card
            className={cn(styles.card)}
            title="Clôturer la session"
            classTitle="title-blue"
            style={{ 
              minHeight: '400px', 
              width: '100%', 
              maxWidth: '600px', 
              position: 'relative',
              maxHeight: '80vh',
              overflow: 'hidden'
            }}
          >
            <div className={styles.description} style={{ 
              maxHeight: 'calc(80vh - 120px)', 
              overflowY: 'auto',
              paddingRight: '10px'
            }}>

              {/* Zone de signature */}
              <div className={styles.signatureSection} style={{ marginBottom: '30px' }}>
                <p style={{ marginBottom: '15px'}}>
                  Merci d'entrer votre signature dans la box ci-dessous :
                </p>
                
                <div className={styles.signatureContainer} style={{ 
                  position: 'relative',
                  backgroundColor: 'white',
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}>
                  <SignatureCanvas
                    ref={signatureRef}
                    canvasProps={{
                      width: canvasWidth,
                      height: 200,
                      className: styles.signatureCanvas,
                      style: {
                        backgroundColor: 'white',
                        cursor: 'crosshair',
                        borderRadius: '4px',
                        maxWidth: '100%'
                      }
                    }}
                  />
                  
                  {/* Texte "SIGNEZ ICI" en arrière-plan */}
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontSize: '24px',
                    color: '#f0f0f0',
                    fontWeight: 'normal',
                    pointerEvents: 'none',
                    zIndex: 0,
                    opacity: 0.6
                  }}>
                    SIGNEZ ICI
                  </div>

                  {/* Bouton croix pour effacer - style modal */}
                  <button
                    type="button"
                    onClick={clearSignature}
                    className={styles.close}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      zIndex: 10
                    }}
                    title="Effacer la signature"
                  >
                    <Icon name="close" size="20" />
                  </button>
                </div>
              </div>

              {session && (
                <div className={styles.sessionInfo} style={{ marginBottom: '20px' }}>
                  <p style={{ textAlign: 'center' }}>
                    J'atteste signer en tant que formateur {session.expand?.teacher_id ? `${session.expand.teacher_id.first_name} ${session.expand.teacher_id.last_name}` : 'Non défini'} pour la session :<br/>
                    {session.title}<br/>
                    {new Date(session.start_date).toLocaleDateString('fr-FR')} à {new Date(session.end_date).toLocaleDateString('fr-FR')}
                  </p>
                </div>
              )}
              
              <div className={styles.btnWrap} style={{ marginTop: '40px', display: 'flex', justifyContent: 'flex-end' }}>
                <button 
                  className="button-blue"
                  onClick={handleCloseSession}
                  disabled={isClosing}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isClosing ? (
                    <>
                      <Icon name="loader" size="16" />
                      Clôture en cours...
                    </>
                  ) : (
                    <>
                      <Icon name="check" size="16" />
                      Clôturer la session
                    </>
                  )}
                </button>
              </div>
            </div>
          </Card>
        </div>
      </div>
    ),
    document.body
  );
};

export default ModalCloseSessionFormation;
